import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import { useCurationCategories } from "@/features/experience-curation/sections/settings/categories/useCurationCategories";
import { EXPERIENCE_CATEGORIES_INTERESTS_QUERY_KEY } from "@/features/experience-curation/queries/useExperienceCategoriesInterestsQuery";

/**
 * Mutation responsible for persisting categories and interests
 * via the metadata-experience API.
 */
export function useCurationCategoriesSaveMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const { put, post, del } = useMetadataExperienceApi();
  const categoriesStore = useCurationCategories();

  async function saveMetadataField(
    endpoint: string,
    fieldKey: string,
    payload: string[],
    existingId: string | undefined
  ) {
    const hasContent = payload.length > 0;
    const body = { experience_id: experienceId.value, [fieldKey]: payload };

    if (hasContent && existingId) {
      await put(`${endpoint}/${existingId}`, { ...body, language_code: "en" });
    } else if (hasContent && !existingId) {
      await post(endpoint, { ...body, language_code: "en" });
    } else if (!hasContent && existingId) {
      await del(`${endpoint}/${existingId}`);
    }
  }

  return useMutation({
    mutationFn: async () => {
      if (!categoriesStore.hasChanges) return;

      await Promise.all([
        saveMetadataField(
          "experience-categories",
          "categories",
          categoriesStore.categories,
          categoriesStore.categoriesRelationId
        ),
        saveMetadataField(
          "experience-interests",
          "interests",
          categoriesStore.interests,
          categoriesStore.interestsRelationId
        ),
      ]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [EXPERIENCE_CATEGORIES_INTERESTS_QUERY_KEY, experienceId.value],
      });
    },
  });
}
